/**
 * Report Download Panel Component
 * Allows CAO and Caredesk owners to download daily, weekly, monthly, and custom date range reports
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { reportsAPI } from '@/lib/api';
import { 
  FileText, 
  Download, 
  Calendar as CalendarIcon, 
  Clock, 
  CalendarDays,
  CalendarRange,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';

const ReportDownloadPanel = ({ user }) => {
  const [downloading, setDownloading] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  // Check if user can download reports
  const canDownload = user?.role === 'cao' || user?.is_caredesk_owner;

  if (!canDownload) {
    return null;
  }

  const handleDownload = async (reportType, customStart = null, customEnd = null) => {
    setDownloading(reportType);
    try {
      let response;
      let filename;
      
      switch (reportType) {
        case 'daily':
          response = await reportsAPI.downloadDailyReport();
          filename = `VGWS_Daily_Report_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`;
          break;
        case 'weekly':
          response = await reportsAPI.downloadWeeklyReport();
          filename = `VGWS_Weekly_Report_${format(new Date(), 'yyyyMMdd')}.pdf`;
          break;
        case 'monthly':
          response = await reportsAPI.downloadMonthlyReport();
          filename = `VGWS_Monthly_Report_${format(new Date(), 'yyyyMM')}.pdf`;
          break;
        case 'custom':
          if (!customStart || !customEnd) {
            toast.error('Please select both start and end dates');
            setDownloading(null);
            return;
          }
          response = await reportsAPI.downloadCustomReport(customStart, customEnd);
          filename = `VGWS_Report_${customStart}_to_${customEnd}.pdf`;
          break;
        default:
          throw new Error('Invalid report type');
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report downloaded successfully!`);
    } catch (error) {
      toast.error('Failed to download report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDownloading(null);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  return (
    <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-slate-700">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#E97832]" />
          Analytics Reports
        </CardTitle>
        <CardDescription className="text-slate-400">
          Download performance reports with resolver staff details
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="quick" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-700/50">
            <TabsTrigger value="quick" className="data-[state=active]:bg-[#E97832]">Quick Reports</TabsTrigger>
            <TabsTrigger value="custom" className="data-[state=active]:bg-[#E97832]">Custom Range</TabsTrigger>
          </TabsList>
          
          <TabsContent value="quick" className="space-y-4 mt-4">
            {/* Daily Report */}
            <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">Daily Report</p>
                  <p className="text-sm text-slate-400">Last 24 hours data</p>
                </div>
              </div>
              <Button 
                onClick={() => handleDownload('daily')}
                disabled={downloading !== null}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="download-daily-report"
              >
                {downloading === 'daily' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download
              </Button>
            </div>
            
            {/* Weekly Report */}
            <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <CalendarDays className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="font-medium">Weekly Report</p>
                  <p className="text-sm text-slate-400">Last 7 days data</p>
                </div>
              </div>
              <Button 
                onClick={() => handleDownload('weekly')}
                disabled={downloading !== null}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="download-weekly-report"
              >
                {downloading === 'weekly' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download
              </Button>
            </div>
            
            {/* Monthly Report */}
            <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <CalendarRange className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-medium">Monthly Report</p>
                  <p className="text-sm text-slate-400">Last 30 days data</p>
                </div>
              </div>
              <Button 
                onClick={() => handleDownload('monthly')}
                disabled={downloading !== null}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="download-monthly-report"
              >
                {downloading === 'monthly' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="custom" className="space-y-4 mt-4">
            <div className="p-4 bg-slate-700/30 rounded-lg border border-slate-600">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="h-5 w-5 text-[#E97832]" />
                <span className="font-medium">Select Date Range</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-slate-300 mb-2 block">Start Date</Label>
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal bg-slate-800 border-slate-600 text-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customStartDate ? customStartDate : "Select start date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customStartDate ? new Date(customStartDate) : undefined}
                        onSelect={(date) => {
                          setCustomStartDate(formatDateForInput(date));
                          setStartDateOpen(false);
                        }}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <Label className="text-slate-300 mb-2 block">End Date</Label>
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal bg-slate-800 border-slate-600 text-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customEndDate ? customEndDate : "Select end date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customEndDate ? new Date(customEndDate) : undefined}
                        onSelect={(date) => {
                          setCustomEndDate(formatDateForInput(date));
                          setEndDateOpen(false);
                        }}
                        disabled={(date) => date > new Date() || (customStartDate && date < new Date(customStartDate))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <Button 
                onClick={() => handleDownload('custom', customStartDate, customEndDate)}
                disabled={downloading !== null || !customStartDate || !customEndDate}
                className="w-full bg-[#E97832] hover:bg-[#d86a28]"
                data-testid="download-custom-report"
              >
                {downloading === 'custom' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Generate & Download Custom Report
              </Button>
              
              {(!customStartDate || !customEndDate) && (
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Please select both start and end dates
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        <div className="mt-4 p-3 bg-slate-700/20 rounded-lg border border-slate-700">
          <p className="text-xs text-slate-400 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            Reports include ticket resolver staff names and performance metrics
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportDownloadPanel;
